import React, { useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { stepPropTypes, stepDefaultProps } from './stepProps';

const money = (n) => (n == null ? null : `$${Number(n).toFixed(2)}`);

const shippingHelp = (product) => [
  'Charged once per order, not per item. With no price set, orders ship free.',
  product.legacy_package_name
    ? `Stored on the pricelist (${product.legacy_package_name}), so it also applies `
      + 'to any other product selling from it.'
    : 'Stored on the pricelist, so it also applies to any other product selling from it.',
  'Digital-only orders are never charged shipping.',
].join(' ');

// Who can buy it, and what it costs them.
//
// These were two steps. Prices had a screen of their own between "what's in it" and
// "how it looks", which put the standard price and the per-school prices on DIFFERENT
// steps -- two screens for one question, and the relationship between them visible on
// neither. They are one grid now: the first row is the price everyone pays, each school
// below may override it, and a blank override falls back to the row above. That is
// exactly what Shop::ProductVariant#price_for resolves at request time (the school's
// own row first, then the global one, then the legacy option's), so the screen and the
// store finally describe the same rule.
//
// Nobody can see the product until a school is allowed to sell it -- the old flow
// buried that on a separate screen reached by a button, easy to finish a product and
// never press.
const Schools = ({
  product, choices, commit, autosave, busy,
}) => {
  const [picked, setPicked] = useState('');
  const attached = new Set(product.schools.map((x) => x.id));
  const addable = choices.assignable_schools.filter((x) => !attached.has(x.id));
  // An add-on attaches to a line that already exists and is never given school access,
  // so for it this step is prices alone. The server owns that rule
  // (Shop::Product#builds_order_package?); this only draws the result.
  const assigns = product.assigns_schools !== false;

  // What a school with no override of its own actually charges: this product's
  // standard price, else the legacy option's own.
  const standardFor = (v) => v.price ?? v.inherited_price;

  const standardHelp = (v) => {
    if (standardFor(v) == null) return 'No price — customers will not see this package';
    if (v.price == null) return `${money(v.inherited_price)} from the pricelist`;
    return null;
  };

  const schoolsNote = () => {
    if (!assigns) return null;
    if (product.schools.length) {
      return (
        <Typography variant="caption" color="text.secondary">
          A blank school price uses the standard one above. Removing revokes access by both routes.
        </Typography>
      );
    }
    return (
      <Typography variant="body2" color="error.main">
        Not available at any school yet — no customer can see this product.
      </Typography>
    );
  };

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {assigns
          ? 'Students at these schools can buy this product. Adding a school gives it access '
            + `to ${product.legacy_package_name || 'the pricelist'}, the pricelist behind it.`
          : 'What this add-on costs. It is offered wherever the product it attaches to is '
            + 'sold, so it is never assigned to schools of its own.'}
      </Typography>

      {assigns ? (
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <TextField
            select
            label="Add a school"
            value={picked}
            sx={{ flex: '1 1 340px' }}
            onChange={(e) => setPicked(e.target.value)}
          >
            <MenuItem value="">— pick a school —</MenuItem>
            {addable.map((x) => <MenuItem key={x.id} value={x.id}>{x.name}</MenuItem>)}
          </TextField>
          <Button
            variant="contained"
            disabled={busy || !picked}
            sx={{ mt: 0.5 }}
            onClick={async () => { await commit({ add_school_ids: picked }); setPicked(''); }}
          >
            Add
          </Button>
        </Stack>
      ) : null}

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{assigns ? 'School' : 'Price'}</TableCell>
            {product.variants.map((v) => (
              <TableCell key={v.id} sx={{ minWidth: 120 }}>{v.name}</TableCell>
            ))}
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {/* The row every school falls back to, at the top of the same grid rather
              than on a step of its own -- "this is the price, unless a school below
              says otherwise" is one sentence and should be one table. */}
          <TableRow>
            <TableCell>
              <Typography variant="body2" fontWeight={600}>
                {assigns ? 'Everyone' : 'Standard'}
              </Typography>
              {assigns ? (
                <Typography variant="caption" color="text.secondary">
                  the standard price
                </Typography>
              ) : null}
            </TableCell>
            {product.variants.map((v) => (
              <TableCell key={v.id}>
                <TextField
                  type="number"
                  defaultValue={v.price ?? ''}
                  placeholder={v.inherited_price != null ? String(v.inherited_price) : 'package price'}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    inputProps: { step: '0.01', min: 0 },
                  }}
                  // An option with no resolvable price is DROPPED by the store, so
                  // that consequence is stated here rather than discovered later.
                  error={standardFor(v) == null}
                  helperText={standardHelp(v)}
                  onChange={(e) => autosave({ prices: { [v.id]: e.target.value } })}
                />
              </TableCell>
            ))}
            <TableCell />
          </TableRow>

          {(assigns ? product.schools : []).map((school, i) => (
            <TableRow key={school.id}>
              <TableCell>{school.name}</TableCell>
              {product.variants.map((v) => (
                <TableCell key={v.id}>
                  <TextField
                    type="number"
                    defaultValue={school.prices[v.id] ?? ''}
                    placeholder={money(standardFor(v)) || 'standard'}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      inputProps: { step: '0.01', min: 0 },
                    }}
                    onBlur={(e) => commit({
                      assignments: {
                        [i]: { school_id: String(school.id), prices: { [v.id]: e.target.value } },
                      },
                    })}
                  />
                </TableCell>
              ))}
              <TableCell>
                <Button
                  color="error"
                  disabled={busy}
                  onClick={() => commit({
                    assignments: { [i]: { school_id: String(school.id), remove: '1' } },
                  })}
                >
                  Remove
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {schoolsNote()}

      {product.ships ? (
        <TextField
          label="Shipping for this order"
          type="number"
          defaultValue={product.shipping_price ?? ''}
          placeholder="e.g. 5.95"
          sx={{ maxWidth: 260 }}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
            inputProps: { step: '0.01', min: 0 },
          }}
          helperText={shippingHelp(product)}
          onChange={(e) => autosave({ shipping: e.target.value })}
        />
      ) : (
        <Alert severity="info">
          This product posts nothing physical, so no shipping is charged — the cart zeroes it for a
          digital-only order.
        </Alert>
      )}
    </Stack>
  );
};

Schools.propTypes = stepPropTypes;
Schools.defaultProps = stepDefaultProps;

export default Schools;
